import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useApp } from "../context/AppContext";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import Seo from "../components/Seo";
import { Wine } from "lucide-react";

export default function Login() {
  const { t, lang } = useApp();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (mode) => async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate(next);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("auth.genericError"));
    } finally {
      setLoading(false);
    }
  };

  const fields = (mode) => (
    <div className="space-y-4">
      {mode === "register" && (
        <Input data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder={t("auth.name")} required className="bg-white h-12" />
      )}
      <Input data-testid={`${mode}-email-input`} type="email" value={form.email} onChange={set("email")} placeholder={t("auth.email")} required className="bg-white h-12" />
      <Input data-testid={`${mode}-password-input`} type="password" value={form.password} onChange={set("password")} placeholder={t("auth.password")} required minLength={6} className="bg-white h-12" />
      {error && <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>}
      <Button data-testid={`${mode}-submit-button`} type="submit" disabled={loading} className="w-full bg-wine hover:bg-wine-dark text-white h-12 text-base">
        {loading ? "…" : mode === "login" ? t("auth.loginCta") : t("auth.registerCta")}
      </Button>
    </div>
  );

  return (
    <div className="pt-20 min-h-screen bg-cream flex items-start justify-center">
      <Seo title={t("auth.loginTitle")} description={t("auth.loginTitle")} path="/login" lang={lang} noindex />
      <div className="w-full max-w-md mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-full bg-wine flex items-center justify-center mb-4"><Wine className="w-6 h-6 text-gold" /></div>
          <h1 className="font-serif text-3xl text-ink">{t("auth.welcome")}</h1>
          <p className="text-ink/60 mt-2">{t("auth.welcomeSub")}</p>
        </div>
        <Tabs defaultValue="login">
          <TabsList className="grid grid-cols-2 w-full mb-6 bg-cream-dark">
            <TabsTrigger data-testid="login-tab" value="login">{t("auth.loginCta")}</TabsTrigger>
            <TabsTrigger data-testid="register-tab" value="register">{t("auth.registerCta")}</TabsTrigger>
          </TabsList>
          <TabsContent value="login">
            <form onSubmit={submit("login")}>{fields("login")}</form>
            <p className="text-right mt-3">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-wine hover:underline underline-offset-4">{t("auth.forgot")}</Link>
            </p>
          </TabsContent>
          <TabsContent value="register"><form onSubmit={submit("register")}>{fields("register")}</form></TabsContent>
        </Tabs>
        <p className="text-center text-sm text-ink/50 mt-8">
          <Link to="/store" className="hover:text-wine underline underline-offset-4">{t("common.continueShopping")}</Link>
        </p>
      </div>
    </div>
  );
}
